<template>
    <v-card>
        <v-card-title>
            <span v-if="!isNewUserMeasurementSet">
                {{ userId }} measurements for {{ taskDisplayName }}
            </span>
            <span v-else>Add measurements for {{ taskDisplayName }}</span>
            <span class="text-body-1 float-right">
                <strong>Anticipated Date:</strong>
                {{ dataCollectionTask.anticipatedDate }} &emsp;<strong
                    >Collection Date Start:</strong
                >
                {{ dataCollectionTask.collectionDateStart || "N/A" }} &emsp;<strong
                    >Collection Date Stop:</strong
                >
                {{ dataCollectionTask.collectionDateStop || "N/A" }}
            </span>
        </v-card-title>
        <v-container fluid>
            <v-row>
                <v-col cols="3" v-if="isNewUserMeasurementSet && isAuthorized('Auth:Manage')">
                    <v-text-field label="User" v-model="userId" />
                    <v-alert
                        v-if="doesUserMeasurementSetExistForUser"
                        density="compact"
                        type="error"
                    >
                        {{ userId }} already has a measurement set for this task. Please specify a
                        different user.
                    </v-alert>
                </v-col>
                <v-col fluid class="text-right">
                    <v-spacer></v-spacer>
                    <v-btn class="mr-2" @click="downloadUserMeasurementsTemplate">
                        Download Template
                    </v-btn>
                    <v-btn
                        v-if="isNewUserMeasurementSet"
                        class="ml-2"
                        :disabled="!isExcelUploadAvailable"
                        @click="openImportUserMeasurementsForm"
                    >
                        Excel Import
                    </v-btn>
                    <v-btn
                        v-else
                        class="ml-2"
                        color="error"
                        :disabled="!isExcelUploadAvailable"
                        @click="openImportUserMeasurementsForm"
                    >
                        Replace Data with Excel Import
                    </v-btn>
                    <v-btn class="ml-2" color="success" @click="flagTaskAsDone">
                        Mark as Done
                    </v-btn>
                </v-col>
            </v-row>
        </v-container>

        <v-data-table
            :headers="thingMeasurementTableHeaders"
            :items="thingDataEntryItems"
            no-results-text="No things."
            class="measurements-table"
        >
            <template
                v-for="slot in thingMeasurementTableSlots"
                v-slot:[`item.${slot.attributeName}`]="{ item }"
                v-bind:key="slot.attributeName"
            >
                <div
                    :class="`${getFieldClass(item, slot.attributeName)} measurement-value`"
                    @mouseenter="showEditButton(item, slot.attributeName)"
                    @mouseleave="hideEditButton(item, slot.attributeName)"
                >
                    <!-- the &nbsp; -->
                    {{ getDisplayValueForMeasurement(item, slot.attributeName) }}
                    <v-icon
                        style="float: right"
                        :disabled="!isThingEditable"
                        :class="getEditIconVisibility(item, slot.attributeName)"
                        @click="showMeasurementForm(item, slot.attributeName)"
                        >$pencil</v-icon
                    >
                </div>
            </template>
            <template v-slot:item.actions="{ item }">
                <v-icon
                    color="primary"
                    @click="showThingForm(item)"
                    :disabled="!isThingEditable"
                    style="margin-left: 10px"
                >
                    $pencil
                </v-icon>
            </template>
        </v-data-table>

        <v-dialog v-model="measurementFormDialogVisible">
            <measurement-form
                v-if="editMeasurementFormData != null"
                :thingSchema="thingSchema"
                :thing="editMeasurementFormData.thing"
                :attribute="editMeasurementFormData.attribute"
                :collection-date-time="editMeasurementFormData.collectionDateTime"
                :value="editMeasurementFormData.value"
                :readout-group="readoutGroup"
                :experiment="experiment"
                :protocol="protocol"
                :default-collection-date-time="collectionDateTime"
                @update-measurement-data-entry-item-value="updateMeasurementValue"
                @cancel-edit-measurement="measurementFormDialogVisible = false"
            />
        </v-dialog>

        <v-dialog v-model="thingFormDialogVisible">
            <edit-thing-measurements-form
                v-if="selectedThing != null"
                :thingSchema="thingSchema"
                :thing="selectedThing"
                :measurements="buildThingMeasurementFormMeasurementsForSelectedThing"
                :data-collection-task="dataCollectionTask"
                :task-schedule="taskSchedule"
                :experiment="experiment"
                :protocol="protocol"
                :readout-group-attributes="readoutGroupAttributes"
                :date-time-config="{
                    dateFieldType,
                    editCollectionDateTimeISOString,
                    collectionDateTimeLabel,
                }"
                @save="saveThingMeasurements"
                @closed="closeThingFormDialog"
            />
        </v-dialog>

        <v-dialog v-model="showImportUserMeasurementsForm" persistent>
            <import-user-measurements-form
                :experiment="experiment"
                :protocol="protocol"
                :dataCollectionTask="dataCollectionTask"
                :collection-date-time="collectionDateTime"
                :collect-user-date="true"
                :readout-group="readoutGroup"
                :user-id="userId"
                :task-display-name="taskDisplayName"
                @saved="savedImportedUserMeasurements"
                @error="displayImportError"
                @closed="closeImportUserMeasurementsForm"
            />
        </v-dialog>
    </v-card>
</template>
<script>
import { defineComponent } from "vue";
import _ from "lodash";
import { measurementsAPI } from "@/services/api.services.ts";
import { withLoadingScreen } from "@/lib/loading.js";
import { mapGetters } from "vuex";
import { getDayForTask, getTaskDisplayName } from "@/lib/taskUtils.js";
import { getThingDisplay, isConditionAvailableOnThingsForSchema } from "@/lib/thingUtils.ts";
import {
    buildCollectionDateTimeFieldConfig,
    buildMeasurementsImportMessage,
    findTaskScheduleObjects,
    getEditabilityForMeasurement,
    getMeasurementCollectionDateTime,
    getMeasurementDisplayValue,
    isSummaryMeasurement,
    MeasurementsHelper,
} from "@/lib/measurementsUtils.js";
import MeasurementForm from "./MeasurementForm.vue";
import ImportUserMeasurementsForm from "./ImportUserMeasurementsForm.vue";
import { assertLuxonDateTime, nowForExperiment, toDateTimeForExperiment } from "@/lib/dateUtils.js";
import { getTrialCodeFromExperimentName } from "@/lib/experimentMetadata";
import EditThingMeasurementsForm from "@/components/Measurements/EditThingMeasurementsForm.vue";
import { downloadFile } from "@/lib/downloadFileUtil.js";
import { DateTime } from "luxon";
import { useToast } from "vue-toastification";
import { thingAPI } from "@/services/things/thingAPI.ts";

export default defineComponent({
    components: { EditThingMeasurementsForm, ImportUserMeasurementsForm, MeasurementForm },

    props: {
        protocol: {
            type: Object,
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
        dataCollectionTask: {
            type: Object,
            required: true,
        },
        taskSchedule: {
            type: Object,
            required: true,
        },
        userMeasurementSet: {
            type: Object,
            required: false, // null if we're creating one
        },
        usersWithMeasurementSets: {
            type: Array,
            required: true,
        },
    },

    setup() {
        return {
            toast: useToast(),
        };
    },

    data() {
        return {
            readoutGroup: null,
            readoutGroupAttributes: [],
            taskDisplayName: "",
            dateFieldType: null,
            editCollectionDateTimeISOString: null,
            collectionDateTimeLabel: null,
            editingUserIdField: false,
            thingSchema: null,
            userId: null,
            measurementsHelper: null,
            // thing measurementData entry table
            things: [],
            thingMeasurementTableHeaders: [],
            thingMeasurementTableSlots: [],
            thingDataEntryItems: [],
            collectionDateTime: null,
            // measurement being edited
            editMeasurementFormData: null,
            // UI stuffs
            measurementFormDialogVisible: false,
            thingFormDialogVisible: false,
            showImportUserMeasurementsForm: false,
            unsavedChanges: false,
            // for bulk operations
            selectedThing: null,
        };
    },

    computed: {
        ...mapGetters({
            user: "getAuthUser",
            isAuthorized: "isAuthorized",
        }),
        isNewUserMeasurementSet() {
            return !this.userMeasurementSet;
        },
        doesUserMeasurementSetExistForUser() {
            return (
                this.isNewUserMeasurementSet && this.usersWithMeasurementSets.includes(this.userId)
            );
        },
        isUserIdSet() {
            return this.userId !== null && !_.isEmpty(this.userId);
        },
        isExcelUploadAvailable() {
            return this.isUserIdSet && !this.doesUserMeasurementSetExistForUser;
        },
        isThingEditable() {
            return this.isUserIdSet && !this.doesUserMeasurementSetExistForUser;
        },
        buildThingMeasurementFormMeasurementsForSelectedThing() {
            const dataEntryItem = this.findThingDataEntryItemForThingId(this.selectedThing.id);
            const measurements = [];
            this.readoutGroupAttributes.forEach((attribute) => {
                const measurementData =
                    dataEntryItem.measurementDataByAttributeName[attribute.name];
                const measurement = measurementData.measurement;
                if (measurement) {
                    measurements.push(measurement);
                }
            });

            return measurements;
        },
    },

    watch: {
        userMeasurementSet: {
            handler: function () {
                if (this.userMeasurementSet) {
                    this.userId = this.userMeasurementSet.userId;
                } else {
                    this.userId = this.user.email;
                }

                // used for new measurement values
                this.collectionDateTime = nowForExperiment(this.experiment);
                this.updateData(this.userMeasurementSet);
            },
            immediate: true,
        },
    },

    methods: {
        async updateData(userMeasurementSet) {
            await withLoadingScreen(async () => {
                const { taskGroup, readoutGroup, readoutGroupAttributes, thingSchema } =
                    await findTaskScheduleObjects({
                        dataCollectionTask: this.dataCollectionTask,
                        experiment: this.experiment,
                        protocol: this.protocol,
                        taskSchedule: this.taskSchedule,
                    });

                this.taskGroup = taskGroup;
                this.readoutGroup = readoutGroup;
                this.thingSchema = thingSchema;
                this.readoutGroupAttributes = readoutGroupAttributes;
                this.things = await thingAPI.fetchFilteredThingsForSchema(
                    this.protocol.code,
                    this.experiment.code,
                    this.thingSchema.id,
                    this.dataCollectionTask.id
                );

                const summaryOneTimeMeasurementSets =
                    await measurementsAPI.fetchSummaryOneTimeMeasurementSets(this.experiment.code);

                const summaryTimeSeriesMeasurementSets =
                    await measurementsAPI.fetchSummaryTimeSeriesMeasurementSets(
                        this.dataCollectionTask.id
                    );

                this.measurementsHelper = new MeasurementsHelper({
                    experiment: this.experiment,
                    protocol: this.protocol,
                    thingSchema: this.thingSchema,
                    summaryOneTimeMeasurementSets,
                    summaryTimeSeriesMeasurementSets,
                    userMeasurementSet,
                });

                this.buildThingDataEntryTable();
                this.updateTaskDisplayName();
                this.updateCollectionDateTimeField();
            });
        },
        updateCollectionDateTimeField() {
            const { dateFieldType, editCollectionDateTimeISOString, collectionDateTimeLabel } =
                buildCollectionDateTimeFieldConfig(
                    this.collectionDateTime,
                    this.readoutGroup,
                    this.experiment
                );
            this.dateFieldType = dateFieldType;
            this.editCollectionDateTimeISOString = editCollectionDateTimeISOString;
            this.collectionDateTimeLabel = collectionDateTimeLabel;
        },
        updateTaskDisplayName() {
            this.taskDisplayName = `${this.taskGroup.displayName} – ${this.readoutGroup.name}`;
        },
        buildThingDataEntryTable() {
            this.thingMeasurementTableHeaders = [
                {
                    title: this.thingSchema.displayName,
                    value: "thingDisplay",
                    sortable: true,
                    align: "left",
                },
            ];

            if (isConditionAvailableOnThingsForSchema(this.thingSchema, this.protocol)) {
                this.thingMeasurementTableHeaders.push(
                    {
                        title: "Condition Number",
                        value: "conditionNumber",
                        sortable: true,
                        align: "left",
                    },
                    {
                        title: "Condition Name",
                        value: "conditionName",
                        sortable: true,
                        align: "left",
                    }
                );
            }

            if (this.thingSchema.hasWellPosition) {
                this.thingMeasurementTableHeaders.push(
                    {
                        title: "Container Name",
                        value: "containerName",
                        sortable: true,
                        align: "left",
                    },
                    {
                        title: "Well Position",
                        value: "wellPosition",
                        sortable: true,
                        align: "left",
                    }
                );
            }

            this.readoutGroupAttributes.forEach((attribute) => {
                this.thingMeasurementTableHeaders.push({
                    title: attribute.display_name,
                    value: attribute.name,
                    sortable: false,
                    align: "left",
                });

                this.thingMeasurementTableSlots.push({
                    attributeName: attribute.name,
                });
            });

            this.thingMeasurementTableHeaders.push({
                title: "Bulk Actions",
                value: "actions",
                sortable: false,
                align: "left",
                width: "10%",
            });

            this.thingDataEntryItems = [];

            this.things.forEach((thing) => {
                const thingDataEntryItem = this.buildDataEntryItemForThingMeasurementSet(thing);
                if (thingDataEntryItem) {
                    this.thingDataEntryItems.push(thingDataEntryItem);
                }
            });
        },
        buildDataEntryItemForThingMeasurementSet(thing) {
            const thingDataEntryItem = {
                thingDisplay: getThingDisplay(thing),
                thing: thing,
                conditionName: thing.condition?.name || "",
                conditionNumber: thing.condition?.number || "",
                containerName: thing.containerWellPosition?.containerName || "",
                wellPosition: thing.containerWellPosition?.wellPosition || "",
                measurementDataByAttributeName: {},
            };

            this.readoutGroupAttributes.forEach((attribute) => {
                this.buildAttributeFormForThingDataEntryItem(thingDataEntryItem, attribute);
            });

            return thingDataEntryItem;
        },
        buildAttributeFormForThingDataEntryItem(thingDataEntryItem, attribute) {
            const attributeName = attribute.name;
            const thing = thingDataEntryItem.thing;

            const measurement = this.measurementsHelper.getLatestMeasurement({
                thing,
                attribute,
            });

            const collectionDateTimeObj = getMeasurementCollectionDateTime(
                measurement,
                this.experiment
            );

            const editability = getEditabilityForMeasurement({
                measurement,
                dataCollectionTask: this.dataCollectionTask,
                experiment: this.experiment,
                taskSchedule: this.taskSchedule,
            });

            // This defines the MeasurementData object.
            thingDataEntryItem.measurementDataByAttributeName[attributeName] = {
                measurement,
                thing,
                collectionDateTime: collectionDateTimeObj,
                isSummaryMeasurement: measurement ? isSummaryMeasurement(measurement) : false,
                attribute,
                showEditButton: false,
                editable: editability.editable,
                disabledMessage: editability.disabledMessage,
            };
        },
        getValueForMeasurement(thingDataEntryItem, attributeName) {
            const measurementData =
                thingDataEntryItem.measurementDataByAttributeName[attributeName];
            const measurement = measurementData.measurement;
            const value = measurement ? measurement.value : null;
            return getMeasurementDisplayValue(
                value,
                attributeName,
                this.thingSchema,
                this.experiment,
                this.protocol
            );
        },
        getDisplayValueForMeasurement(thingDataEntryItem, attributeName) {
            const value = this.getValueForMeasurement(thingDataEntryItem, attributeName);
            if (_.isNil(value)) {
                return "No value";
            } else {
                return value;
            }
        },
        hasMeasurementValue(thingDataEntryItem, attributeName) {
            return !_.isNil(this.getValueForMeasurement(thingDataEntryItem, attributeName));
        },
        getFieldClass(thingDataEntryItem, attributeName) {
            const measurementData =
                thingDataEntryItem.measurementDataByAttributeName[attributeName];

            if (measurementData.editable) {
                return this.hasMeasurementValue(thingDataEntryItem, attributeName)
                    ? ""
                    : "no-measurement";
            } else {
                return "disabled-measurement";
            }
        },
        showEditButton(thingDataEntryItem, attributeName) {
            const measurementData =
                thingDataEntryItem.measurementDataByAttributeName[attributeName];
            if (measurementData.editable) {
                measurementData.showEditButton = true;
            }
        },
        hideEditButton(thingDataEntryItem, attributeName) {
            thingDataEntryItem.measurementDataByAttributeName[attributeName].showEditButton = false;
        },
        getEditIconVisibility(thingDataEntryItem, attributeName) {
            return thingDataEntryItem.measurementDataByAttributeName[attributeName].showEditButton
                ? "edit-icon-visible"
                : "edit-icon-hidden";
        },
        showMeasurementForm(thingDataEntryItem, attributeName) {
            const measurementData =
                thingDataEntryItem.measurementDataByAttributeName[attributeName];

            const value = _.isNil(measurementData.measurement)
                ? null
                : measurementData.measurement.value;

            // set the required edit fields here and only here
            this.editMeasurementFormData = {
                thing: thingDataEntryItem.thing,
                attribute: measurementData.attribute,
                collectionDateTime: measurementData.collectionDateTime,
                value,
            };
            this.openMeasurementFormDialog();
        },
        showThingForm(thingDataEntryItem) {
            this.selectedThing = thingDataEntryItem.thing;
            this.openThingFormDialog();
            console.log("showThingForm", thingDataEntryItem);
        },
        findThingDataEntryItemForThingId(thingId) {
            return this.thingDataEntryItems.find((thingDataEntryItem) => {
                return thingDataEntryItem.thing.id === thingId;
            });
        },
        /**
         * Update a single Measurement value, which then gets mapped to a UserMeasurement
         * @param thing
         * @param attribute
         * @param value
         * @param collectionDateTime
         */
        updateMeasurementValue({ thing, attribute, value, collectionDateTime }) {
            assertLuxonDateTime(collectionDateTime);
            const isUpdatedValue = this.measurementsHelper.isMeasurementValueUpdated({
                thing,
                attribute,
                value,
                collectionDateTime,
            });

            if (isUpdatedValue) {
                this.measurementsHelper.updateMeasurement({
                    thingSchema: this.thingSchema,
                    thing,
                    attribute,
                    value,
                    collectionDateTime,
                });

                this.unsavedChanges = true;
                this.save();
            }

            this.closeMeasurementFormDialog();
        },
        openMeasurementFormDialog() {
            this.measurementFormDialogVisible = true;
        },
        closeMeasurementFormDialog() {
            this.measurementFormDialogVisible = false;
        },
        openThingFormDialog() {
            this.thingFormDialogVisible = true;
        },
        closeThingFormDialog() {
            this.thingFormDialogVisible = false;
        },
        /**
         * Called by EditThingMeasurementsForm to update multiple Measurements for a Thing
         */
        saveThingMeasurements(measurements, measurementDateTime) {
            const collectionDateTime = measurementDateTime
                ? toDateTimeForExperiment(measurementDateTime, this.experiment)
                : toDateTimeForExperiment(this.editCollectionDateTimeISOString, this.experiment);

            const thingDataEntryItem = this.findThingDataEntryItemForThingId(this.selectedThing.id);
            const thing = thingDataEntryItem.thing;

            const updated = this.measurementsHelper.replaceThingMeasurements({
                thing,
                measurements,
                collectionDateTime,
            });

            if (updated) {
                this.unsavedChanges = true;
                this.save();
            }

            this.closeThingFormDialog();
            this.selectedThing = null;
        },
        /**
         * type ImportWorkbookResult = {
         *   importMeasurementSetResults: ImportMeasurementSetResult[];
         *   skippedSheets: string[];
         * };
         * export type ImportMeasurementSetResult = {
         *     worksheetName: string;
         *     skippedAttributes: string[];
         *     userMeasurementSets: SavedUserMeasurementSet[];
         *     skippedRows: string[];
         * };
         * @param userMeasurementImportResult
         */
        savedImportedUserMeasurements(userMeasurementImportResult) {
            const result = userMeasurementImportResult.importMeasurementSetResults[0];
            const userMeasurementSet = result.userMeasurementSets[0];
            this.savedUserMeasurementSets(
                userMeasurementSet,
                result.skippedRows,
                result.skippedAttributes,
                userMeasurementImportResult.skippedSheets
            );
            this.closeImportUserMeasurementsForm();
        },
        savedUserMeasurementSets(
            userMeasurementSet,
            skippedRows = [],
            skippedAttributes = [],
            skippedSheets = []
        ) {
            if (!_.isNil(skippedSheets) && skippedSheets.length > 0) {
                // There's only a single sheet for this form, so if it's skipped it means the upload
                // was unsuccessful.
                this.toast.error(skippedSheets.join(", "), { timeout: false });
            } else {
                const message = buildMeasurementsImportMessage({ skippedRows, skippedAttributes });
                this.unsavedChanges = false;
                this.toast.success(message, { timeout: false });
                this.updateData(userMeasurementSet);
            }
        },
        async flagTaskAsDone() {
            await withLoadingScreen(async () => {
                const status = "DONE";
                const timestamp = new Date().toISOString();
                await measurementsAPI.setTaskStatusForTask(
                    this.dataCollectionTask.id,
                    status,
                    timestamp
                );
            });
        },
        openImportUserMeasurementsForm() {
            this.showImportUserMeasurementsForm = true;
        },
        displayImportError(error) {
            this.toast.error(error);
            this.showImportUserMeasurementsForm = false;
        },
        closeImportUserMeasurementsForm() {
            this.showImportUserMeasurementsForm = false;
        },
        cancel() {
            this.$emit("closed");
        },
        save() {
            withLoadingScreen(async () => {
                const userMeasurementSet = this.measurementsHelper.userMeasurementSet;
                const savedUserMeasurementSet = await measurementsAPI.saveUserMeasurementSet({
                    ...userMeasurementSet,
                    userId: this.userId,
                    dataCollectionTaskId: this.dataCollectionTask.id,
                });
                this.savedUserMeasurementSets(savedUserMeasurementSet);
            });
        },
        async downloadUserMeasurementsTemplate() {
            await withLoadingScreen(async () => {
                const response = await measurementsAPI.downloadUserMeasurementsTemplate(
                    this.dataCollectionTask.id,
                    this.userId
                );
                const filename = this.buildMeasurementsTemplateFilename();
                downloadFile(filename, response);
            });
        },
        buildMeasurementsTemplateFilename() {
            const filenameParts = [];
            const trialCode = getTrialCodeFromExperimentName(this.experiment.name);
            if (trialCode) {
                filenameParts.push(trialCode);
            }
            const day = getDayForTask(
                this.dataCollectionTask,
                this.taskGroup,
                this.experiment.day0
            );
            filenameParts.push(this.experiment.code);
            if (this.readoutGroup && this.readoutGroup.name) {
                filenameParts.push(this.readoutGroup.name);
            }
            if (day !== null) {
                filenameParts.push(`Day ${day}`);
            }
            const filename = filenameParts.join("-");
            return `${filename}.xlsx`;
        },
    },
});
</script>
<style>
/* This is the Thing ID - give it "normal" padding */
.measurements-table td:first-child {
    padding: 0 10px !important;
}

/* These are the measurement value cells - give them less space to consolidate the table */
.measurements-table td {
    border-left: 1px solid #e0e0e0;
    padding: 0 2px !important;
}

.measurement-value {
    /* Without this, the value doesn't fill the whole cell, resulting in the mouseover edit icon being hard to find */
    height: 100%;
    /* align the edit icon & values in the middle of the cell */
    display: flex;
    justify-content: center;
    align-items: center;
}

.no-measurement {
    color: #ccc;
}

.disabled-measurement {
    color: #ccc;
}

.measurement-value:hover {
    cursor: pointer;
}

.edit-icon-visible {
    visibility: visible;
}

.edit-icon-hidden {
    visibility: hidden;
}
</style>
